import { MAX_PLAYERS, type RosterEntry, type Team } from '../net/protocol';

/**
 * THE FRONT DOOR.
 *
 * DOM for the same reasons the HUD is DOM, and written to the same rule: this is
 * signage inside a corporate space, not a game menu. No rounded corners, no
 * gradients, no icons. A player arriving here should feel they are reading a
 * staff noticeboard in a mall that closed at nine.
 *
 * It holds no netcode and no game state. It renders what it is handed and calls
 * back when a button is pressed, which is what lets the same screen serve solo,
 * host and client without knowing which of the three it is looking at.
 *
 * FOUR LETTERS. The room code is the entire matchmaking system. It is what one
 * person reads out loud to another over voice chat, so it avoids the characters
 * that get misheard — no O against 0, no I against 1 — and it is what the URL
 * carries, so the alternative to reading it out is pasting a link.
 */

export interface LobbyHandlers {
  onSolo(): void;
  onHost(name: string): void;
  onJoin(code: string, name: string): void;
  onTeam(team: Team): void;
  onReady(ready: boolean): void;
  onStart(): void;
  onLeave(): void;
  /** Desktop build only: open Steam's own invite dialog. Absent on the web. */
  onInvite?(): void;
}

/** No O/0 or I/1 confusion, because this gets said out loud. */
const ALPHABET = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';

export function makeRoomCode(): string {
  let s = '';
  const r = new Uint8Array(4);
  crypto.getRandomValues(r);
  for (const b of r) s += ALPHABET[b % ALPHABET.length];
  return s;
}

export class Lobby {
  private readonly root: HTMLDivElement;
  private readonly front: HTMLDivElement;
  private readonly room: HTMLDivElement;
  private readonly nameInput: HTMLInputElement;
  private readonly codeInput: HTMLInputElement;
  private readonly statusEl: HTMLDivElement;
  private readonly codeEl: HTMLDivElement;
  private readonly linkEl: HTMLButtonElement;
  private readonly listEl: HTMLDivElement;
  private readonly startBtn: HTMLButtonElement;
  private readonly readyBtn: HTMLButtonElement;
  private readonly h: LobbyHandlers;
  private readonly joinRow!: HTMLDivElement;
  private self = '';
  private code = '';
  private steam = false;

  constructor(parent: HTMLElement, handlers: LobbyHandlers) {
    this.h = handlers;
    injectLobbyStyle();
    this.root = div('lobby');

    const card = div('lb-card');
    const title = div('lb-title');
    title.textContent = 'NIGHT SHIFT';
    const sub = div('lb-sub');
    sub.textContent = 'MERIDIAN CROSSING · AFTER HOURS';
    card.append(title, sub);

    // --- front -------------------------------------------------------------
    this.front = div('lb-pane on');
    const nameRow = div('lb-row');
    const nameLabel = div('lb-label');
    nameLabel.textContent = 'NAME';
    this.nameInput = document.createElement('input');
    this.nameInput.className = 'lb-input';
    this.nameInput.maxLength = 18;
    this.nameInput.spellcheck = false;
    this.nameInput.value = savedName();
    this.nameInput.addEventListener('input', () => saveName(this.nameInput.value));
    nameRow.append(nameLabel, this.nameInput);

    const solo = button('lb-btn primary', 'PLAY ALONE', () => this.h.onSolo());
    const host = button('lb-btn', 'HOST A CREW', () => this.h.onHost(this.name()));

    const joinRow = div('lb-row');
    this.codeInput = document.createElement('input');
    this.codeInput.className = 'lb-input code';
    this.codeInput.maxLength = 4;
    this.codeInput.spellcheck = false;
    this.codeInput.placeholder = 'CODE';
    this.codeInput.addEventListener('input', () => {
      this.codeInput.value = this.codeInput.value.toUpperCase().replace(/[^A-Z0-9]/g, '');
    });
    this.codeInput.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') this.tryJoin();
    });
    const joinBtn = button('lb-btn narrow', 'JOIN', () => this.tryJoin());
    joinRow.append(this.codeInput, joinBtn);
    this.joinRow = joinRow;

    const note = div('lb-note');
    note.textContent =
      'Thieves have one night and one life. Guards have the cameras and the keys. ' +
      'Take what you can carry, post it at a chute for 65%, or walk it to the dock for full price.';

    this.statusEl = div('lb-status');
    this.front.append(nameRow, solo, host, joinRow, note, this.statusEl);

    // --- room --------------------------------------------------------------
    this.room = div('lb-pane');
    const codeWrap = div('lb-codewrap');
    const codeLabel = div('lb-label');
    codeLabel.textContent = 'ROOM';
    this.codeEl = div('lb-code');
    this.linkEl = button('lb-link', 'COPY LINK', () => {
      if (this.steam) this.h.onInvite?.();
      else void this.copyLink();
    });
    codeWrap.append(codeLabel, this.codeEl, this.linkEl);

    this.listEl = div('lb-list');
    const teamRow = div('lb-teams');
    teamRow.append(
      button('lb-btn narrow', 'THIEF', () => this.h.onTeam('thief')),
      button('lb-btn narrow', 'GUARD', () => this.h.onTeam('guard')),
    );
    this.readyBtn = button('lb-btn', 'READY', () => {
      const next = !this.readyBtn.classList.contains('on');
      this.readyBtn.classList.toggle('on', next);
      this.h.onReady(next);
    });
    this.startBtn = button('lb-btn primary', 'OPEN THE DOORS', () => this.h.onStart());
    const leave = button('lb-btn quiet', 'LEAVE', () => this.h.onLeave());
    this.room.append(codeWrap, this.listEl, teamRow, this.readyBtn, this.startBtn, leave);

    card.append(this.front, this.room);
    this.root.appendChild(card);
    parent.appendChild(this.root);
  }

  get visible(): boolean {
    return this.root.classList.contains('on');
  }

  show(): void {
    this.root.classList.add('on');
  }

  hide(): void {
    this.root.classList.remove('on');
  }

  /** A code prefilled from the URL, so a shared link needs one click. */
  prefill(code: string): void {
    this.codeInput.value = code.toUpperCase().slice(0, 4);
  }

  name(): string {
    return this.nameInput.value.trim() || 'Crew';
  }

  status(text: string, bad = false): void {
    this.statusEl.textContent = text;
    this.statusEl.classList.toggle('bad', bad);
  }

  /**
   * Switch the front page over to the desktop build's way of finding friends.
   *
   * A four-letter room code exists because a browser has no idea who you know.
   * Steam does, so on the desktop build the code is not a worse version of the
   * friends list — it is a second, parallel way in, and two ways in is two sets
   * of bugs and a player wondering which one their friend used. The row goes,
   * the name comes from the persona rather than a text box, and the button that
   * copied a URL opens the overlay instead.
   */
  steamMode(persona: string): void {
    this.steam = true;
    this.joinRow.style.display = 'none';
    if (persona) {
      this.nameInput.value = persona;
      this.nameInput.disabled = true;
    }
    this.linkEl.textContent = 'INVITE FRIENDS';
  }

  /** Front to room. Called once the relay has given us a slot. */
  enterRoom(code: string, self: string): void {
    this.code = code;
    this.self = self;
    this.codeEl.textContent = code;
    this.front.classList.remove('on');
    this.room.classList.add('on');
    this.status('');
  }

  leaveRoom(): void {
    this.room.classList.remove('on');
    this.front.classList.add('on');
    this.readyBtn.classList.remove('on');
  }

  setRoster(roster: readonly RosterEntry[], canStart: boolean): void {
    this.listEl.replaceChildren();
    for (const r of roster) {
      const row = div(`lb-peer${r.id === this.self ? ' me' : ''}`);
      row.innerHTML =
        `<span class="p-slot">${String(r.slot + 1).padStart(2, '0')}</span>` +
        `<span class="p-name">${esc(r.name)}</span>` +
        `<span class="p-team ${r.team}">${r.team.toUpperCase()}</span>` +
        `<span class="p-ping">${r.host ? 'HOST' : r.ping ? `${r.ping}MS` : '—'}</span>` +
        `<span class="p-ready">${r.ready ? '●' : '○'}</span>`;
      this.listEl.appendChild(row);
    }
    for (let i = roster.length; i < Math.min(MAX_PLAYERS, 8); i++) {
      const row = div('lb-peer empty');
      row.textContent = 'OPEN';
      this.listEl.appendChild(row);
    }
    // Only the host can open the doors, and a lobby with no thief in it is a
    // round nobody can win, so the button says why rather than doing nothing.
    this.startBtn.style.display = canStart ? '' : 'none';
  }

  private tryJoin(): void {
    const code = this.codeInput.value.trim().toUpperCase();
    if (code.length < 4) {
      this.status('A ROOM CODE IS FOUR CHARACTERS', true);
      return;
    }
    this.h.onJoin(code, this.name());
  }

  private async copyLink(): Promise<void> {
    const url = `${location.origin}${location.pathname}?room=${this.code}`;
    try {
      await navigator.clipboard.writeText(url);
      this.linkEl.textContent = 'COPIED';
    } catch {
      // Clipboard access is refused often enough — over plain http, in an
      // unfocused tab — that failing silently would look like a dead button.
      this.linkEl.textContent = url;
    }
    setTimeout(() => (this.linkEl.textContent = this.steam ? 'INVITE FRIENDS' : 'COPY LINK'), 2200);
  }
}

// ---------------------------------------------------------------------------

function div(cls: string): HTMLDivElement {
  const n = document.createElement('div');
  n.className = cls;
  return n;
}

function button(cls: string, text: string, on: () => void): HTMLButtonElement {
  const b = document.createElement('button');
  b.className = cls;
  b.textContent = text;
  b.addEventListener('click', on);
  return b;
}

function esc(s: string): string {
  return s.replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' })[c]!);
}

const NAME_KEY = 'meridian.name';

function savedName(): string {
  try {
    return localStorage.getItem(NAME_KEY) ?? '';
  } catch {
    return '';
  }
}

function saveName(v: string): void {
  try {
    localStorage.setItem(NAME_KEY, v.slice(0, 18));
  } catch {
    /* private browsing */
  }
}

let styled = false;
function injectLobbyStyle(): void {
  if (styled) return;
  styled = true;
  const s = document.createElement('style');
  s.textContent = CSS;
  document.head.appendChild(s);
}

const CSS = `
.lobby{position:fixed;inset:0;z-index:20;display:none;place-items:center;
  background:radial-gradient(120% 90% at 50% 40%,rgba(6,10,16,.62),rgba(4,6,10,.9));
  backdrop-filter:blur(4px);
  font:11px/1.6 ui-monospace,"SF Mono",Menlo,monospace;letter-spacing:.16em;
  color:#cfd8e4;text-transform:uppercase}
.lobby.on{display:grid}
.lb-card{width:min(420px,90vw);padding:34px 38px 30px;
  background:rgba(9,13,19,.9);outline:1px solid rgba(140,175,210,.18)}
.lb-title{font-size:26px;letter-spacing:.5em;color:#eaf1fa;text-align:center;
  text-shadow:0 0 24px rgba(120,170,220,.22)}
.lb-sub{font-size:8px;letter-spacing:.42em;color:#7f92a8;text-align:center;margin-top:9px}

.lb-pane{display:none;flex-direction:column;gap:9px;margin-top:26px}
.lb-pane.on{display:flex}
.lb-row{display:flex;gap:9px;align-items:center}
.lb-label{font-size:8px;letter-spacing:.4em;color:#7f92a8;min-width:44px}
.lb-input{flex:1;background:rgba(4,7,11,.7);border:0;outline:1px solid rgba(120,150,180,.22);
  color:#eaf1fa;font:inherit;letter-spacing:.2em;padding:9px 11px;text-transform:uppercase}
.lb-input:focus{outline-color:#8fd7ff}
.lb-input.code{flex:1;letter-spacing:.55em;text-align:center;font-size:15px}
.lb-input::placeholder{color:#4b5a6b;letter-spacing:.4em;font-size:10px}

.lb-btn{background:rgba(4,7,11,.7);border:0;outline:1px solid rgba(120,150,180,.22);
  color:#c3d0de;font:inherit;letter-spacing:.34em;font-size:10px;padding:11px 14px;
  cursor:pointer;transition:background .12s,color .12s,outline-color .12s}
.lb-btn:hover{background:rgba(143,215,255,.1);color:#eaf1fa;outline-color:rgba(143,215,255,.5)}
.lb-btn.primary{background:#8fd7ff;color:#0c1219;outline:0}
.lb-btn.primary:hover{background:#b3e5ff}
.lb-btn.narrow{padding:11px 16px;flex:1}
.lb-btn.quiet{outline-color:rgba(120,150,180,.12);color:#7f92a8;font-size:9px;padding:8px}
.lb-btn.on{outline-color:#8ee6b4;color:#8ee6b4}

.lb-note{font-size:10px;line-height:1.75;color:#7f92a8;text-transform:none;
  letter-spacing:.03em;margin-top:8px}
.lb-status{font-size:9px;letter-spacing:.3em;color:#8fd7ff;min-height:14px;text-align:center}
.lb-status.bad{color:#ff8161}

.lb-codewrap{display:flex;align-items:center;gap:12px}
.lb-code{font-size:26px;letter-spacing:.42em;color:#8fd7ff;flex:1;
  text-shadow:0 0 18px rgba(120,200,255,.35)}
.lb-link{background:none;border:0;outline:1px solid rgba(120,150,180,.2);color:#7f92a8;
  font:inherit;font-size:8px;letter-spacing:.28em;padding:6px 9px;cursor:pointer}
.lb-link:hover{color:#eaf1fa;outline-color:rgba(143,215,255,.5)}

.lb-list{display:flex;flex-direction:column;gap:2px;margin:4px 0}
.lb-peer{display:grid;grid-template-columns:24px 1fr auto 46px 14px;gap:10px;align-items:center;
  font-size:10px;padding:5px 9px;background:rgba(12,18,26,.55);
  border-left:2px solid rgba(150,180,210,.22)}
.lb-peer.me{border-left-color:#8fd7ff;background:rgba(143,215,255,.07)}
.lb-peer.empty{color:#3f4c5c;background:none;border-left-color:transparent;
  display:block;padding-left:11px;font-size:9px;letter-spacing:.4em}
.p-slot{color:#55667a;font-variant-numeric:tabular-nums}
.p-name{color:#dce6f2;text-transform:none;letter-spacing:.08em;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
.p-team{font-size:8px;letter-spacing:.24em;padding:2px 6px}
.p-team.thief{color:#8ee6b4;outline:1px solid rgba(142,230,180,.3)}
.p-team.guard{color:#e8c07a;outline:1px solid rgba(232,192,122,.3)}
.p-ping{font-size:8px;letter-spacing:.2em;color:#55667a;text-align:right;font-variant-numeric:tabular-nums}
.p-ready{color:#8ee6b4;font-size:9px}
.lb-teams{display:flex;gap:8px}
`;
